/**
 * Room 画面: コード表示 + Web Share / Copy + 参加者リスト + 設定 (host のみ編集) + Start。
 * 2 人揃って host が start を押すと WS で `{ t: 'start' }` を送信、サーバから `started` で
 * playing 遷移 → Play 画面へ。
 *
 * phase ごとの分岐:
 *  - connecting: ローダ
 *  - lobby: 本コンポーネント (参加者 + 開始)
 *  - playing: <Play />
 *  - finished: <Result />
 *  - closed: 切断表示
 */

import { useEffect, useMemo, useState } from 'react';
import { ConnectionBadge } from '../components/ConnectionBadge';
import { ModePicker } from '../components/ModePicker';
import { GamePicker } from '../components/GamePicker';
import { LoadingScreen } from '../components/Loading';
import { TitleBar } from '../components/TitleBar';
import { navigate } from '../lib/navigate';
import { createRoomCheck } from '../lib/room-check';
import { fetchWithTurnstileRetry } from '../lib/turnstile';
import type { MatchConfig } from '../proto';
import { useRoom } from '../state/room';
import { Play } from './Play';
import { Result } from './Result';

// StrictMode mounts share the cookie-establishing request while it is pending.
const checkRoom = createRoomCheck(fetchWithTurnstileRetry);

interface TimeControlPreset {
  id: string;
  label: string;
  initial_ms: number;
  increment_ms: number;
}

const TIME_PRESETS: TimeControlPreset[] = [
  { id: '3+0', label: '3分', initial_ms: 3 * 60_000, increment_ms: 0 },
  { id: '5+3', label: '5分+3秒', initial_ms: 5 * 60_000, increment_ms: 3_000 },
  { id: '10+0', label: '10分', initial_ms: 10 * 60_000, increment_ms: 0 },
  { id: '15+10', label: '15分+10秒', initial_ms: 15 * 60_000, increment_ms: 10_000 },
  { id: '45+0', label: '45分', initial_ms: 45 * 60_000, increment_ms: 0 },
  { id: '120+0', label: '2時間', initial_ms: 120 * 60_000, increment_ms: 0 },
];

function ClockPolicyPicker({
  config,
  isHost,
  onChange,
}: {
  config: MatchConfig;
  isHost: boolean;
  onChange: (next: MatchConfig) => void;
}) {
  return (
    <div className="time-control">
      <div className="time-control-label">
        <span className="time-control-eyebrow">進行モード</span>
        {!isHost && <small className="muted">ホストのみ変更可</small>}
      </div>
      <ModePicker config={config} isHost={isHost} onChange={onChange} />
    </div>
  );
}

function TimeControl({
  config,
  isHost,
  onChange,
}: {
  config: MatchConfig;
  isHost: boolean;
  onChange: (next: MatchConfig) => void;
}) {
  const current = presetIdForClock(config.clock);
  return (
    <div className="time-control">
      <div className="time-control-label">
        <span className="time-control-eyebrow">時間制限</span>
        {!isHost && <small className="muted">ホストのみ変更可</small>}
      </div>
      <div className="time-control-options">
        {TIME_PRESETS.map((p) => {
          const active = current === p.id;
          return (
            <button
              key={p.id}
              className={`time-control-option ${active ? 'active' : ''}`}
              disabled={!isHost}
              onClick={() => {
                // 旧 scope を継承して新 clock JSON を組み立て (モード変更時は別途上書きされる)。
                const curScope =
                  (config.clock as { scope?: string } | undefined)?.scope ?? 'Whole';
                onChange({
                  ...config,
                  clock: {
                    scope: curScope,
                    increment:
                      p.increment_ms > 0
                        ? { type: 'Fischer', increment_ms: p.increment_ms }
                        : { type: 'None' },
                    initial_ms: p.initial_ms,
                  },
                });
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function presetIdForClock(clock: unknown): string | null {
  if (!clock || typeof clock !== 'object') return null;
  const c = clock as {
    initial_ms?: number;
    increment?: { type?: string; increment_ms?: number };
  };
  const incMs =
    c.increment?.type === 'Fischer' ? (c.increment.increment_ms ?? 0) : 0;
  for (const p of TIME_PRESETS) {
    if (p.initial_ms === c.initial_ms && p.increment_ms === incMs) {
      return p.id;
    }
  }
  return null;
}

/**
 * `flag` が立ってから `delayMs` 後に true を返す (下りは即座)。
 * 決着した瞬間に結果画面へ飛ばすと最後の一手が見えないので、 少し盤を残すのに使う。
 */
function useDelayed(flag: boolean, delayMs: number): boolean {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!flag) {
      setDone(false);
      return;
    }
    const t = window.setTimeout(() => setDone(true), delayMs);
    return () => window.clearTimeout(t);
  }, [flag, delayMs]);
  return done;
}

export function Room({ code }: { code: string }) {
  const phase = useRoom((s) => s.phase);
  // 決着から結果画面までの間。 最後の一手を見せてから切り替える。
  const resultReady = useDelayed(phase === 'finished', 1400);
  const participants = useRoom((s) => s.participants);
  const config = useRoom((s) => s.config);
  const you = useRoom((s) => s.you);
  const reconnecting = useRoom((s) => s.reconnecting);
  const lastError = useRoom((s) => s.lastError);
  const connect = useRoom((s) => s.connect);
  const disconnect = useRoom((s) => s.disconnect);
  const start = useRoom((s) => s.start);
  const updateConfig = useRoom((s) => s.updateConfig);
  const [lobbyListed, setLobbyListed] = useState(true);

  // mount 時に GET /api/rooms/:code で「アクティブ部屋として存在するか」 を確認。
  // 404 (= 存在しない / 既に finished or aborted) ならエラー画面 + ホームリンクを出す。
  // 200 なら通常通り WS connect。 直リンクで来た未認証ユーザーは Turnstile gate を通る
  // (= fetchWithTurnstileRetry が 403 を見たら token 取って 1 度だけ retry)。
  // この時点で発行された guest cookie で続く WS upgrade が認証通過する。
  const [roomCheckError, setRoomCheckError] = useState<'not_found' | 'network' | null>(null);
  // NOTE: 以前は Room mount で prefetchTurnstile() して token を温めていたが、
  // managed site key では challenge widget が即表示されて出っぱなしになるため撤去。
  // token は必要時 (fetchWithTurnstileRetry の 403 リトライ) に取りに行く。
  useEffect(() => {
    let cancelled = false;
    setRoomCheckError(null);
    setLobbyListed(true);
    checkRoom(code)
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setRoomCheckError('not_found');
          return;
        }
        if (res.ok) {
          const info = await res.clone().json() as { lobby_listed?: boolean };
          if (cancelled) return;
          setLobbyListed(info.lobby_listed !== false);
        }
        // 200/その他: そのまま WS へ。 401 等で REST が見えなくても WS 側で再認証されるので
        // ここでは network エラー扱いしない。
        connect(code);
      })
      .catch(() => {
        if (cancelled) return;
        setRoomCheckError('network');
      });
    return () => {
      cancelled = true;
      disconnect();
    };
  }, [code, connect, disconnect]);

  const canStart = useMemo(() => {
    return you === 'host' && !!config && config.games.length > 0 && config.games.length <= 8 && participants.some((p) => p.role === 'guest');
  }, [you, participants, config]);

  const [copied, setCopied] = useState(false);
  async function shareCode() {
    // 「現在ブラウザに出ている URL」 をそのままシェア。 tailscale 経由など origin が
    // window.location.origin と一致しないケースでも、 ユーザーが今いるリンクをそのまま
    // 渡せる方が直感的。 query/hash も維持される。
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'mixed-games', text: `部屋コード: ${code}`, url });
        return;
      } catch {
        /* fallthrough */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }

  if (roomCheckError === 'not_found') {
    return (
      <main className="centered">
        <div className="status-card">
          <h2>部屋が見つかりません</h2>
          <p className="muted">
            コード <code>{code}</code> の部屋は存在しないか、 既に終了しています。
          </p>
          <a className="btn-primary" href="/" data-testid="room-not-found-home">
            ホームに戻る
          </a>
        </div>
      </main>
    );
  }
  if (roomCheckError === 'network') {
    return (
      <main className="centered">
        <div className="status-card">
          <h2>接続できませんでした</h2>
          <p className="muted">サーバーに到達できません。 ネットワークを確認してください。</p>
          <a className="btn-primary" href="/">
            ホームに戻る
          </a>
          <button className="btn-secondary" onClick={() => location.reload()}>
            再読み込み
          </button>
        </div>
      </main>
    );
  }
  if (phase === 'connecting' || phase === 'closed') {
    return <LoadingScreen label="サーバーに接続中" />;
  }
  if (phase === 'playing') {
    return <Play code={code} />;
  }
  if (phase === 'finished') {
    // 決着直後は盤のまま止める。 入力は phase で塞がっているので、 見せるだけ。
    return resultReady ? <Result /> : <Play code={code} />;
  }

  return (
    <main className="app-shell">
      {reconnecting && <div className="banner">再接続中…</div>}
      <TitleBar
        title="ロビー"
        back={{ href: '/' }}
        center={<ConnectionBadge />}
        menu={[
          { label: '設定', onClick: () => navigate('/settings') },
          {
            label: '部屋を出る',
            variant: 'danger',
            onClick: () => navigate('/'),
          },
        ]}
      />
      <div className="room-code">
        <div>
          <div className="label">部屋コード</div>
          <div className="value">{code}</div>
        </div>
        <div className="grow" />
        <button className="btn-secondary" onClick={shareCode}>
          {copied ? 'コピー済' : 'シェア'}
        </button>
      </div>
      {!lobbyListed && <p className="muted" data-testid="room-unlisted">ロビー非表示 · URLや部屋コードで招待できます</p>}
      <section className="room-actions">
        <button
          className="btn-primary"
          onClick={start}
          disabled={!canStart}
        >
          {you === 'host' ? (canStart ? '対局を開始' : 'ゲストの参加待ち…') : 'ホストの開始待ち…'}
        </button>
      </section>
      <section>
        <h3>参加者</h3>
        <ul className="chip-list">
          {participants.map((p) => (
            <li key={p.sub} className="chip" data-role={p.role}>
              <span>{p.display_name}</span>
              <span className="chip-role">{p.role}</span>
            </li>
          ))}
        </ul>
      </section>
      {config && (
        <section>
          <h3>設定</h3>
          <GamePicker
            config={config}
            isHost={you === 'host'}
            onChange={(c) => updateConfig(c)}
          />
          <TimeControl
            config={config}
            isHost={you === 'host'}
            onChange={(c) => updateConfig(c)}
          />
          <ClockPolicyPicker
            config={config}
            isHost={you === 'host'}
            onChange={(c) => updateConfig(c)}
          />
        </section>
      )}
      {lastError && <p className="error">{lastError}</p>}
      <section className="room-actions">
        <button
          className="btn-primary"
          onClick={start}
          disabled={!canStart}
          data-testid="start"
        >
          {you === 'host' ? (canStart ? '対局を開始' : 'ゲストの参加待ち…') : 'ホストの開始待ち…'}
        </button>
      </section>
    </main>
  );
}
